import React, { useState } from "react";
import { Link } from "react-router-dom";

/**
 * SalesKit — the field-sales presentation surface.
 *
 * Two views (toggle):
 *  1. PRESENT  — full-screen 3-card demo flow optimized for an iPad held up
 *                in front of a contractor at a supply-house counter / job site.
 *  2. SCRIPT   — printable one-page script + objection responses + close lines.
 *
 * The 3 demo cards walk the contractor through:
 *  Card 1: "Last week you missed X calls. Here's what they cost."
 *  Card 2: "Here's what happens with ARIA on duty." (live caller demo + transcript)
 *  Card 3: "Here's the Monday receipt." (real Dollars-Saved email mock)
 *
 * Close: gold "Sign Up Now" button → /onboard-tablet?rep=THOMAS
 */
const REP_DEFAULT = "thomas";

const NAV = { color: "#0A1628", background: "#fff" };
const GOLD = { color: "#D4AF37" };
const NAVY_BG = { background: "#0A1628", color: "#fff" };

const cards = [
  {
    id: "miss",
    label: "1 · THE MISS",
    title: "LAST WEEK YOU MISSED ABOUT 11 CALLS.",
    subtitle: "Each one was worth $400 to $2,000.",
    body: [
      "Average RI contractor misses 8–15 calls per week.",
      "About 1 in 3 callers buys from the next contractor they call.",
      "That's $400 to $2,000 walking to your competitor — every week.",
    ],
    cta: "OK, BUT WHAT'S THE FIX?",
    nextLabel: "SHOW ME WHAT ARIA DOES →",
  },
  {
    id: "aria",
    label: "2 · ARIA ON DUTY",
    title: "THIS IS ARIA ANSWERING YOUR PHONE.",
    subtitle: "Sounds like a real receptionist. Asks the right questions for your trade.",
    body: [
      '"Hi, this is the front desk at Joe\'s Plumbing — how can I help?"',
      '— Caller: "I have a leak under my sink, can someone come today?"',
      '"Absolutely. What\'s your address, and how old is the water heater? I\'ll have Joe call you back in 15 minutes with an ETA."',
      "→ You get an email + SMS in under 60 seconds: name, phone, address, urgency, scope.",
      "→ If you don't respond in 2 hours, NearNow flags it and sends the lead to a backup contractor.",
    ],
    cta: "THAT'S WHAT I PAY $97 FOR?",
    nextLabel: "SHOW ME THE RECEIPT →",
  },
  {
    id: "receipt",
    label: "3 · THE RECEIPT",
    title: "EVERY MONDAY YOU GET THIS EMAIL.",
    subtitle: "Real dollars caught. Real proof of value.",
    body: [
      "Subject: ARIA saved you $4,200 last week",
      "  6 leads captured · 2 urgent escalations caught · 3 jobs won",
      "  Estimated $ saved: $4,200 — that's 43× your $97 subscription",
      "  Breakdown: 2 urgent · 3 this week · 1 planning",
      "→ Email also lands in your inbox every Monday. Cancel anytime if it's not paying for itself.",
    ],
    cta: "LET'S SIGN UP",
    nextLabel: "START MY FREE MONTH →",
  },
];

const SalesKit = () => {
  const [view, setView] = useState("present"); // 'present' | 'script'
  const [idx, setIdx] = useState(0);
  const rep = (new URLSearchParams(window.location.search).get("rep") || REP_DEFAULT).toLowerCase();
  const closeHref = `/onboard-tablet?rep=${encodeURIComponent(rep)}`;

  return (
    <div data-testid="sales-kit-page" style={{ minHeight: "100vh", background: "#F8F9FA" }}>
      {/* Top bar */}
      <div style={{ ...NAVY_BG, padding: "14px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontFamily: "Bebas Neue, sans-serif", letterSpacing: 3, fontSize: 18, ...GOLD }}>
          NEARNOW · FIELD SALES KIT · REP: {rep.toUpperCase()}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            data-testid="sales-kit-present-tab"
            onClick={() => setView("present")}
            className="tap"
            style={{
              background: view === "present" ? "#D4AF37" : "transparent",
              color: view === "present" ? "#0A1628" : "#cbd0db",
              border: "1px solid #D4AF37", padding: "8px 16px",
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
              borderRadius: 999, cursor: "pointer", fontSize: 14,
            }}
          >
            PRESENT
          </button>
          <button
            data-testid="sales-kit-script-tab"
            onClick={() => setView("script")}
            className="tap"
            style={{
              background: view === "script" ? "#D4AF37" : "transparent",
              color: view === "script" ? "#0A1628" : "#cbd0db",
              border: "1px solid #D4AF37", padding: "8px 16px",
              fontFamily: "Bebas Neue, sans-serif", letterSpacing: 2,
              borderRadius: 999, cursor: "pointer", fontSize: 14,
            }}
          >
            SCRIPT
          </button>
        </div>
      </div>

      {view === "present" ? (
        <DemoCards idx={idx} setIdx={setIdx} closeHref={closeHref} />
      ) : (
        <ScriptPage closeHref={closeHref} />
      )}
    </div>
  );
};

const DemoCards = ({ idx, setIdx, closeHref }) => {
  const card = cards[idx];
  const last = idx === cards.length - 1;
  const onNext = () => {
    if (last) {
      window.location.href = closeHref;
    } else {
      setIdx(idx + 1);
    }
  };
  return (
    <div data-testid="demo-card-stage" style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 80px" }}>
      <p style={{ color: "#D4AF37", letterSpacing: 4, fontSize: 14, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
        {card.label}
      </p>
      <h1
        data-testid={`demo-card-title-${card.id}`}
        className="font-display"
        style={{ fontSize: 72, lineHeight: 1.02, margin: "10px 0 8px", color: "#0A1628", letterSpacing: 1 }}
      >
        {card.title}
      </h1>
      <p style={{ fontSize: 24, color: "#5a5d66", margin: "0 0 30px" }}>
        {card.subtitle}
      </p>

      <div
        data-testid={`demo-card-body-${card.id}`}
        style={{
          background: "#fff", borderRadius: 16, padding: 36,
          boxShadow: "0 24px 60px rgba(10,22,40,0.10)",
          fontSize: 22, lineHeight: 1.5, color: "#1A1A2E",
        }}
      >
        {card.body.map((line, i) => (
          <p key={i} style={{ margin: i === 0 ? 0 : "14px 0 0", paddingLeft: line.startsWith("→") ? 0 : 12 }}>
            {line.startsWith("→") ? (
              <span style={{ color: "#1A7A4A", fontWeight: 700 }}>{line}</span>
            ) : line.startsWith("—") ? (
              <span style={{ color: "#A32D2D" }}>{line}</span>
            ) : (
              line
            )}
          </p>
        ))}
      </div>

      {/* Nav */}
      <div style={{ display: "flex", gap: 16, marginTop: 28, alignItems: "center" }}>
        {idx > 0 && (
          <button
            data-testid="demo-card-back"
            onClick={() => setIdx(idx - 1)}
            className="btn-outline-navy tap"
            style={{ minWidth: 140 }}
          >
            ← BACK
          </button>
        )}
        <button
          data-testid="demo-card-next"
          onClick={onNext}
          className="btn-gold tap"
          style={{ flex: 1, fontSize: 26, minHeight: 80, letterSpacing: 1 }}
        >
          {card.nextLabel}
        </button>
      </div>

      {/* Progress dots */}
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 24 }}>
        {cards.map((_, i) => (
          <button
            key={i}
            data-testid={`demo-card-dot-${i}`}
            onClick={() => setIdx(i)}
            aria-label={`Go to card ${i + 1}`}
            style={{
              width: 14, height: 14, borderRadius: 999,
              border: "none", cursor: "pointer",
              background: i === idx ? "#D4AF37" : "#cbd0db",
            }}
          />
        ))}
      </div>
    </div>
  );
};

const ScriptPage = ({ closeHref }) => (
  <div data-testid="sales-kit-script" style={{ maxWidth: 820, margin: "0 auto", padding: "40px 24px 80px" }}>
    <h1 className="font-display" style={{ fontSize: 56, color: "#0A1628", letterSpacing: 1, margin: "0 0 6px" }}>
      THOMAS FIELD SCRIPT
    </h1>
    <p style={{ color: "#5a5d66", fontSize: 17, margin: 0 }}>
      One-page printable. The flow: open → demo → handle objections → close.
    </p>

    {/* Open */}
    <Section title="OPEN · 30 SECONDS">
      <p>
        {`"Hey {NAME} — quick question. About how many calls do you miss in a week when you're on a job site? … Right. Most contractors I talk to lose 8 to 15. At maybe $800 a call, that's eight grand a week walking to your competitor. I can show you how NearNow catches every one of them for $97 a month — first month's free. Got 4 minutes?"`}
      </p>
    </Section>

    {/* Demo */}
    <Section title="DEMO · 3 MINUTES">
      <ol style={{ paddingLeft: 22, lineHeight: 1.7, fontSize: 17 }}>
        <li><strong>Hand them the iPad on Card 1 (The Miss).</strong> Read it with them. Get them to nod.</li>
        <li><strong>Tap to Card 2 (ARIA on Duty).</strong> Read the script out loud — let them hear what ARIA sounds like for their trade.</li>
        <li><strong>Tap to Card 3 (The Receipt).</strong> Point at the $4,200. {`"This is what shows up in your inbox every Monday morning."`}</li>
        <li><strong>Hand them the iPad on the Sign-Up screen.</strong> They type their info, you stand back and watch them tap.</li>
      </ol>
    </Section>

    {/* Objections */}
    <Section title="OBJECTIONS · 30 SECONDS EACH">
      <Obj q="I already have an answering service.">
        Most answering services charge $80–$200 a month and just take messages.
        ARIA actually asks the right questions for your trade — water heater age for
        plumbing, panel amperage for electrical — and the lead lands in your phone
        in under a minute. Cancel anytime. First month is free. What do you have to lose?
      </Obj>
      <Obj q="$97 is too much.">
        {`First month is on us, so it's $0 today. After that, ARIA needs to catch you one $800 job a month to pay for itself 8× over. If she doesn't, you cancel — no penalty. That's why we do the free month.`}
      </Obj>
      <Obj q="I'm too busy to set this up.">
        I literally do it right now on this iPad. 5 fields. 2 minutes. ARIA is
        answering your phone before you leave the parking lot. Want me to do it now?
      </Obj>
      <Obj q="I'll think about it.">
        {`Totally fair. While you're thinking about it though — your phone's still ringing and you're still missing calls. The free month means there's nothing to think about. Worst case, you cancel in 30 days. Best case, you keep $4,200 you would have lost. Tap to start?`}
      </Obj>
    </Section>

    {/* Close */}
    <Section title="CLOSE · 15 SECONDS">
      <p>
        {`"Let's do it. I'll hand you the iPad — 5 fields, 2 minutes, ARIA is answering by tonight. You can cancel anytime from the email I'm about to send you. Ready?"`}
      </p>
      <Link
        to={closeHref}
        data-testid="sales-kit-script-cta"
        className="btn-gold tap"
        style={{ display: "inline-block", marginTop: 12, padding: "16px 28px", fontSize: 22, letterSpacing: 1 }}
      >
        OPEN THE SIGN-UP SCREEN →
      </Link>
    </Section>

    <Section title="THE NUMBERS · USE IF ASKED">
      <ul style={{ paddingLeft: 22, lineHeight: 1.7, fontSize: 16 }}>
        <li>Avg RI contractor misses 8–15 calls/week.</li>
        <li>~1 in 3 callers buys from the NEXT contractor they call.</li>
        <li>Gold (NearNow Autopilot) = $97/mo, includes ARIA · Your AI Receptionist.</li>
        <li>2-hour urgent escalation guarantee. Cancel anytime.</li>
        <li>First month free for founding contractors (first 100).</li>
        <li>Stripe checkout — real card on file, charged at day 31.</li>
      </ul>
    </Section>
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ marginTop: 30 }}>
    <p style={{ color: "#D4AF37", letterSpacing: 3, fontSize: 13, fontFamily: "Bebas Neue, sans-serif", margin: 0 }}>
      {title}
    </p>
    <div style={{
      marginTop: 8, background: "#fff", padding: 22, borderRadius: 10,
      border: "1px solid #e5e7ee", fontSize: 17, lineHeight: 1.55, color: "#1A1A2E",
    }}>
      {children}
    </div>
  </div>
);

const Obj = ({ q, children }) => (
  <div style={{ marginBottom: 14 }}>
    <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#A32D2D", fontSize: 16 }}>
      They say: {`"${q}"`}
    </p>
    <p style={{ margin: 0, color: "#1A1A2E", fontSize: 16, lineHeight: 1.55 }}>
      <strong style={{ color: "#1A7A4A" }}>You say:</strong> {children}
    </p>
  </div>
);

export default SalesKit;
